/**
 * @file Parent-board Firestore listener orchestration for existing parent collections.
 */

import {
    collection,
    limit,
    onSnapshot,
    orderBy,
    query,
    where,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";
import { db } from "../firebase/firebase.js";
import {
    activeListeners,
    appData,
    state,
} from "../state/state.js";
import {
    renderAssetBoard,
    renderLabsBoard,
    renderMaintenanceBoard,
    renderPatientBoard,
    renderProjectBoard,
} from "../view/viewController.js";
import {
    renderDetailsView,
} from "../detail/detailController.js";
import { updateAuditTrailUI } from "../../shared/details/detailSections.js";
import {
    getBoardRegistryEntries,
    isBoardCollectionListenerEnabled,
} from "../../shared/board/boardRegistry.js";
import {
    getItemType,
} from "../../shared/utils/itemLookup.js";

const BOARD_RENDERERS_BY_VIEW = Object.freeze({
    patients: renderPatientBoard,
    projects: renderProjectBoard,
    assets: renderAssetBoard,
    labs: renderLabsBoard,
    maintenance: renderMaintenanceBoard,
});

function buildParentCollectionSpecs() {
    return getBoardRegistryEntries()
        .filter((board) => isBoardCollectionListenerEnabled(board.viewKey))
        .map((board) => Object.freeze({
            viewKey: board.viewKey,
            collectionType: board.collectionName,
            selectedType: board.itemType,
            renderBoard: BOARD_RENDERERS_BY_VIEW[board.viewKey] || null,
            openErrorMessage:
                `Error listening to open ${board.collectionName}:`,
            closedErrorMessage:
                `Error listening to recent closed ${board.collectionName}:`,
        }))
        .filter((spec) => typeof spec.renderBoard === "function");
}

const PARENT_COLLECTION_SPECS = Object.freeze(buildParentCollectionSpecs());

function getUpdatedAtValue(item) {
    const updatedAt = item?.updatedAt;
    if (!updatedAt) {return 0;}

    if (typeof updatedAt?.toMillis === "function") {
        return updatedAt.toMillis();
    }
    if (typeof updatedAt?.seconds === "number") {
        const nanos = typeof updatedAt?.nanoseconds === "number" ?
            updatedAt.nanoseconds :
            0;
        return (updatedAt.seconds * 1000) + Math.floor(nanos / 1000000);
    }

    const parsed = new Date(updatedAt);
    return Number.isNaN(parsed.getTime()) ? 0 : parsed.getTime();
}

function mergeUniqueById(...collections) {
    const merged = new Map();
    collections.flat().forEach((item) => {
        if (!item?.id) {return;}

        const existing = merged.get(item.id);
        if (!existing || getUpdatedAtValue(item) >= getUpdatedAtValue(existing)) {
            merged.set(item.id, item);
        }
    });

    return Array.from(merged.values());
}

function mapSnapshotDocs(snapshot) {
    return snapshot.docs.map((snapshotDoc) => ({
        id: snapshotDoc.id,
        ...snapshotDoc.data(),
    }));
}

/**
 * Refreshes boards whose display depends on another parent collection.
 * @param {string} collectionType Updated collection name.
 * @returns {void}
 */
export function refreshDependentParentViewsForCollection(collectionType) {
    if (
        collectionType === "assets" &&
        state.currentView === "maintenance" &&
        !state.selectedItemId
    ) {
        renderMaintenanceBoard();
    }

    if (
        collectionType === "maintenance" &&
        state.selectedItemId &&
        getItemType(state.selectedItemId) === "asset"
    ) {
        renderDetailsView();
    }
}

function updateParentCollection(spec, openItems, recentClosedItems) {
    const paginationState = state.closedItemsPagination[spec.collectionType];
    const combinedItems = mergeUniqueById(
        openItems,
        recentClosedItems,
        paginationState.extraItems || [],
    );

    appData[spec.collectionType] = combinedItems;

    if (state.currentView === spec.viewKey && !state.selectedItemId) {
        spec.renderBoard();
    }
    refreshDependentParentViewsForCollection(spec.collectionType);
    if (
        state.selectedItemId &&
        getItemType(state.selectedItemId) === spec.selectedType
    ) {
        updateAuditTrailUI(state.selectedItemId);
    }
}

function attachParentCollectionSpecListeners(spec, closedItemsLimit) {
    let openItems = [];
    let recentClosedItems = [];

    const openQuery = query(
        collection(db, spec.collectionType),
        where("isClosedResolved", "==", false),
        orderBy("updatedAt", "desc"),
    );
    const openListener = onSnapshot(openQuery, (snapshot) => {
        openItems = mapSnapshotDocs(snapshot);
        updateParentCollection(spec, openItems, recentClosedItems);
    }, (error) => console.error(spec.openErrorMessage, error));
    activeListeners.push(openListener);

    const closedQuery = query(
        collection(db, spec.collectionType),
        where("isClosedResolved", "==", true),
        orderBy("updatedAt", "desc"),
        limit(closedItemsLimit),
    );
    const closedListener = onSnapshot(closedQuery, (snapshot) => {
        recentClosedItems = mapSnapshotDocs(snapshot);
        const paginationState = state.closedItemsPagination[spec.collectionType];
        if (!paginationState.hasLoadedMore) {
            paginationState.lastDoc = snapshot.docs.length > 0 ?
                snapshot.docs[snapshot.docs.length - 1] :
                null;
            paginationState.hasMore = snapshot.docs.length === closedItemsLimit;
        }
        updateParentCollection(spec, openItems, recentClosedItems);
    }, (error) => console.error(spec.closedErrorMessage, error));
    activeListeners.push(closedListener);
}

/**
 * Attaches open and recent-closed listeners for current parent board collections.
 * @param {number} closedItemsLimit - Maximum recent closed items per listener.
 */
export function attachParentCollectionListeners(closedItemsLimit) {
    PARENT_COLLECTION_SPECS.forEach((spec) => {
        attachParentCollectionSpecListeners(spec, closedItemsLimit);
    });
}
